(() => {
  if (window.LnCollectionEnhancementsLoaded) return;
  window.LnCollectionEnhancementsLoaded = true;

  const SCROLLER_SELECTOR = '[data-ln-collection-scroller]';
  const variantCache = new WeakMap();
  const mediaFrameCache = new WeakMap();
  let openVariantSheet = null;

  const syncVisibleProductCount = (toolbar) => {
    if (!toolbar) return;
    const visibleCount = toolbar.querySelector(':scope > .ln-editorial-grid__count');
    const dawnCount = toolbar.querySelector('.product-count__text');
    if (visibleCount && dawnCount?.textContent.trim()) {
      visibleCount.textContent = dawnCount.textContent.trim();
    }
  };

  const scrollByCard = (scroller, direction) => {
    const card = scroller.querySelector('[data-ln-scroll-card], [data-ln-look-card]');
    if (!card) return;

    const gap = Number.parseFloat(window.getComputedStyle(scroller).columnGap || 0);
    const amount = (card.getBoundingClientRect().width + gap) * direction;
    scroller.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const getMediaIndex = (track) => {
    const width = track.clientWidth;
    if (!width) return 0;
    return Math.max(0, Math.min(track.children.length - 1, Math.round(track.scrollLeft / width)));
  };

  const updateCardMedia = (track) => {
    if (!track) return;
    const card = track.closest('[data-ln-product-card]');
    const index = getMediaIndex(track);
    const hasMultipleImages = track.children.length > 1;

    card?.querySelectorAll('[data-ln-card-media-dot]').forEach((dot, dotIndex) => {
      const active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });

    const previous = card?.querySelector('[data-ln-card-media-previous]');
    const next = card?.querySelector('[data-ln-card-media-next]');
    if (previous) previous.disabled = !hasMultipleImages;
    if (next) next.disabled = !hasMultipleImages;
  };

  const queueCardMediaUpdate = (track) => {
    if (mediaFrameCache.has(track)) return;
    const frame = window.requestAnimationFrame(() => {
      mediaFrameCache.delete(track);
      updateCardMedia(track);
    });
    mediaFrameCache.set(track, frame);
  };

  const moveCardMedia = (card, targetIndex) => {
    const track = card?.querySelector('[data-ln-card-media-track]');
    if (!track || !track.children.length) return;

    const imageCount = track.children.length;
    const index = ((targetIndex % imageCount) + imageCount) % imageCount;
    track.scrollTo({
      left: track.clientWidth * index,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  };

  const getVariants = (sheet) => {
    if (variantCache.has(sheet)) return variantCache.get(sheet);

    let variants = [];
    try {
      variants = JSON.parse(sheet.querySelector('[data-ln-variant-json]')?.textContent || '[]');
    } catch (error) {
      console.error('[LumiNova Collection variants]', error);
    }

    variantCache.set(sheet, variants);
    return variants;
  };

  const selectedOptions = (sheet) => (
    Array.from(sheet.querySelectorAll('[data-ln-option-group]'))
      .sort((a, b) => Number(a.dataset.lnOptionIndex) - Number(b.dataset.lnOptionIndex))
      .map((group) => group.querySelector('[data-ln-option-value].is-selected')?.dataset.lnOptionValue || '')
  );

  const updateVariantSheet = (sheet) => {
    if (!sheet) return;

    const options = selectedOptions(sheet);
    const variant = getVariants(sheet).find((item) => (
      Array.isArray(item.options)
      && item.options.length === options.length
      && item.options.every((value, index) => value === options[index])
    ));

    const input = sheet.querySelector('[data-ln-variant-id]');
    const submit = sheet.querySelector('[data-ln-add-button]');
    const card = sheet.closest('[data-ln-product-card]');
    const price = card?.querySelector('[data-ln-card-price]');
    const originalPrice = card?.querySelector('[data-ln-card-original-price]');
    const available = Boolean(variant?.available);

    if (input) {
      input.value = variant?.id || '';
      input.disabled = !available;
    }
    if (submit) submit.disabled = !available;

    if (price && variant?.price) price.textContent = variant.price;
    if (price) price.classList.toggle('is-discounted', Boolean(variant?.originalPrice));
    if (originalPrice) {
      originalPrice.hidden = !variant?.originalPrice;
      originalPrice.textContent = variant?.originalPrice || '';
    }

  };

  const closeSheet = (sheet, restoreFocus = true) => {
    if (!sheet) return;
    const card = sheet.closest('[data-ln-product-card]');
    const trigger = card?.querySelector('[data-ln-variant-sheet-open]');

    sheet.setAttribute('aria-hidden', 'true');
    card?.classList.remove('is-variant-open');
    trigger?.setAttribute('aria-expanded', 'false');
    if (openVariantSheet === sheet) openVariantSheet = null;

    if (restoreFocus) trigger?.focus({ preventScroll: true });
  };

  const openSheet = (trigger) => {
    const card = trigger.closest('[data-ln-product-card]');
    const sheetId = trigger.getAttribute('aria-controls');
    const sheet = sheetId ? document.getElementById(sheetId) : null;
    if (!card || !sheet) return;

    if (openVariantSheet && openVariantSheet !== sheet) closeSheet(openVariantSheet, false);
    updateVariantSheet(sheet);
    card.classList.add('is-variant-open');
    sheet.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    openVariantSheet = sheet;

    window.requestAnimationFrame(() => {
      sheet.querySelector('[data-ln-option-value].is-selected, [data-ln-variant-sheet-close]')?.focus({ preventScroll: true });
    });
  };

  const setAddState = (form, state, message = '') => {
    const button = form.querySelector('[data-ln-add-button]');
    const label = button?.querySelector('[data-ln-add-label]');
    const cardStatus = form.closest('[data-ln-product-card]')?.querySelector('[data-ln-card-status]');
    if (!button || !label) return;

    const labels = {
      default: button.dataset.defaultLabel || 'Add',
      loading: button.dataset.addingLabel || 'Adding…',
      added: button.dataset.addedLabel || 'Added',
      error: button.dataset.errorLabel || 'Try again'
    };

    button.classList.toggle('is-loading', state === 'loading');
    button.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    button.disabled = state === 'loading';
    label.textContent = labels[state] || labels.default;
    if (cardStatus) cardStatus.textContent = message;
  };

  const submitCollectionForm = async (form) => {
    if (form.dataset.lnSubmitting === 'true') return;
    const button = form.querySelector('[data-ln-add-button]');
    const variantInput = form.querySelector('[name="id"]');
    if (!button || button.disabled || !variantInput?.value) return;

    form.dataset.lnSubmitting = 'true';
    setAddState(form, 'loading');

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const sections = [];

      if (typeof cartDrawer?.getSectionsToRender === 'function') {
        cartDrawer.getSectionsToRender().forEach((section) => sections.push(section.id));
      } else if (typeof cartNotification?.getSectionsToRender === 'function') {
        cartNotification.getSectionsToRender().forEach((section) => sections.push(section.id));
      } else {
        sections.push('cart-drawer', 'cart-icon-bubble');
      }

      const formData = new FormData(form);
      formData.append('sections', [...new Set(sections)].join(','));
      formData.append('sections_url', window.location.pathname);

      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || button.dataset.errorLabel || 'Add to cart failed');

      const sheet = form.closest('[data-ln-variant-sheet]');
      if (sheet) closeSheet(sheet, false);

      if (typeof cartDrawer?.renderContents === 'function') {
        cartDrawer.classList.remove('is-empty');
        cartDrawer.setActiveElement?.(button);
        cartDrawer.renderContents(data);
      } else if (typeof cartNotification?.renderContents === 'function') {
        cartNotification.setActiveElement?.(button);
        cartNotification.renderContents(data);
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: data }));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      }

      setAddState(form, 'added');
      window.setTimeout(() => setAddState(form, 'default'), 1400);
    } catch (error) {
      console.error('[LumiNova Collection add to cart]', error);
      setAddState(form, 'error', error.message || button.dataset.errorLabel || 'Add to cart failed');
      window.setTimeout(() => setAddState(form, 'default'), 2200);
    } finally {
      form.dataset.lnSubmitting = 'false';
    }
  };

  const loadMoreProducts = async (link) => {
    if (link.dataset.loading === 'true') return;

    const container = link.closest('.ln-editorial-grid__container');
    const grid = container?.querySelector('#product-grid');
    if (!container || !grid) return;

    const originalLabel = link.textContent;
    link.dataset.loading = 'true';
    link.setAttribute('aria-disabled', 'true');
    link.textContent = link.dataset.loadingLabel || 'Loading…';

    try {
      const response = await fetch(link.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = documentFragment.querySelector('#product-grid');
      const nextWrap = documentFragment.querySelector('[data-ln-load-more-wrap]');
      if (!nextGrid || !nextWrap) throw new Error('Collection response is missing the product grid.');

      nextGrid.querySelectorAll('.ln-editorial-grid__product').forEach((card) => {
        grid.appendChild(card);
        updateCardMedia(card.querySelector('[data-ln-card-media-track]'));
      });

      const currentWrap = container.querySelector('[data-ln-load-more-wrap]');
      if (currentWrap) currentWrap.replaceWith(nextWrap);
    } catch (error) {
      link.dataset.loading = 'false';
      link.removeAttribute('aria-disabled');
      link.textContent = originalLabel;
      console.error(error);
    }
  };

  document.addEventListener('click', (event) => {
    const loadMoreLink = event.target.closest('[data-ln-load-more]');
    if (loadMoreLink) {
      event.preventDefault();
      loadMoreProducts(loadMoreLink);
      return;
    }

    const mediaPrevious = event.target.closest('[data-ln-card-media-previous]');
    const mediaNext = event.target.closest('[data-ln-card-media-next]');
    const mediaDot = event.target.closest('[data-ln-card-media-dot]');
    if (mediaPrevious || mediaNext || mediaDot) {
      event.preventDefault();
      const card = event.target.closest('[data-ln-product-card]');
      const track = card?.querySelector('[data-ln-card-media-track]');
      const currentIndex = getMediaIndex(track);
      const targetIndex = mediaDot
        ? Number(mediaDot.dataset.lnCardMediaDot)
        : currentIndex + (mediaPrevious ? -1 : 1);
      moveCardMedia(card, targetIndex);
      return;
    }

    const sheetOpen = event.target.closest('[data-ln-variant-sheet-open]');
    if (sheetOpen) {
      event.preventDefault();
      openSheet(sheetOpen);
      return;
    }

    const sheetClose = event.target.closest('[data-ln-variant-sheet-close]');
    if (sheetClose) {
      event.preventDefault();
      closeSheet(sheetClose.closest('[data-ln-product-card]')?.querySelector('[data-ln-variant-sheet]'));
      return;
    }

    const option = event.target.closest('[data-ln-option-value]');
    if (option) {
      event.preventDefault();
      const group = option.closest('[data-ln-option-group]');
      group?.querySelectorAll('[data-ln-option-value]').forEach((item) => {
        const selected = item === option;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      updateVariantSheet(option.closest('[data-ln-variant-sheet]'));
      return;
    }

    const button = event.target.closest('[data-ln-scroll-button]');
    if (!button) return;

    const target = document.getElementById(button.dataset.lnScrollTarget);
    if (!target) return;
    scrollByCard(target, Number(button.dataset.lnScrollDirection || 1));
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-ln-collection-product-form]');
    if (!form) return;

    event.preventDefault();
    event.stopPropagation();
    submitCollectionForm(form);
  });

  document.addEventListener('scroll', (event) => {
    if (event.target instanceof Element && event.target.matches('[data-ln-card-media-track]')) {
      queueCardMediaUpdate(event.target);
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openVariantSheet) {
      event.preventDefault();
      closeSheet(openVariantSheet);
      return;
    }

    const mediaTrack = event.target.closest('[data-ln-card-media-track]');
    if (mediaTrack && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault();
      const card = mediaTrack.closest('[data-ln-product-card]');
      moveCardMedia(card, getMediaIndex(mediaTrack) + (event.key === 'ArrowLeft' ? -1 : 1));
      return;
    }

    const scroller = event.target.closest(SCROLLER_SELECTOR);
    if (!scroller || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;

    event.preventDefault();
    scrollByCard(scroller, event.key === 'ArrowLeft' ? -1 : 1);
  });

  document.querySelectorAll('[data-ln-card-media-track]').forEach(updateCardMedia);
  document.querySelectorAll('.ln-editorial-grid__toolbar').forEach(syncVisibleProductCount);

  const countObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      const node = mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : mutation.target.parentElement;
      const dawnCount = node?.closest?.('.product-count__text');
      if (dawnCount) syncVisibleProductCount(dawnCount.closest('.ln-editorial-grid__toolbar'));
    });
  });
  countObserver.observe(document.body, { childList: true, characterData: true, subtree: true });
})();
